import { getJson, sendJson } from '@/lib/api';

/**
 * Outreach email templates (repeat clients, Phase C): each rep's own
 * templates and the team's. Mirrors the API's
 * `outreach-templates.service.ts` views and `placeholders.ts`.
 */

/** Sales → My email templates. */
export const OUTREACH_TEMPLATES_PATH = '/sales/email-templates';
/** The id segment that opens a blank template instead of a saved one. */
export const NEW_TEMPLATE = 'new';
/** Where team templates live, below the list. */
const TEAM_SEGMENT = 'team';
/** Route patterns for App.tsx: one rep template, one team template. */
export const OUTREACH_TEMPLATE_ROUTE = `${OUTREACH_TEMPLATES_PATH}/:templateId`;
export const TEAM_OUTREACH_TEMPLATE_ROUTE = `${OUTREACH_TEMPLATES_PATH}/${TEAM_SEGMENT}/:templateId`;

export const OUTREACH_TEMPLATE_SCOPE = { personal: 'personal', team: 'team' } as const;
export type OutreachTemplateScope = (typeof OUTREACH_TEMPLATE_SCOPE)[keyof typeof OUTREACH_TEMPLATE_SCOPE];

/** A template, as the API sends it. */
export interface OutreachTemplate {
  readonly id: string;
  readonly scope: OutreachTemplateScope;
  readonly name: string;
  readonly whenToUse: string;
  readonly subject: string;
  readonly body: string;
  readonly version: number;
  readonly updatedAt: string;
}

/** What the template form saves. */
export interface OutreachTemplateInput {
  readonly name: string;
  readonly whenToUse: string;
  readonly subject: string;
  readonly body: string;
}

/** The team's templates, and whether the viewer may edit them. */
export interface TeamTemplates {
  readonly templates: readonly OutreachTemplate[];
  readonly canManage: boolean;
}

/** A placeholder the picker offers, with the sample the preview fills in. */
export interface OutreachPlaceholder {
  readonly key: string;
  readonly label: string;
  readonly sample: string;
}

/** Field limits, matching the API's schemas. */
export const OUTREACH_TEMPLATE_LIMITS = {
  name: 80,
  whenToUse: 200,
  subject: 200,
  body: 5_000,
} as const;

export const OUTREACH_QUERY_KEYS = {
  mine: ['outreach-templates', 'mine'],
  team: ['outreach-templates', 'team'],
  placeholders: ['outreach-templates', 'placeholders'],
} as const;

/**
 * The page for one template, or for a new one.
 *
 * @param scope - The rep's own, or the team's.
 * @param id - The template, or {@link NEW_TEMPLATE}.
 * @returns The app path.
 */
export function outreachTemplatePath(scope: OutreachTemplateScope, id: string): string {
  return scope === OUTREACH_TEMPLATE_SCOPE.team
    ? `${OUTREACH_TEMPLATES_PATH}/${TEAM_SEGMENT}/${id}`
    : `${OUTREACH_TEMPLATES_PATH}/${id}`;
}

const BASE = '/outreach-templates';
const scopePath = (scope: OutreachTemplateScope): string => `${BASE}/${scope === OUTREACH_TEMPLATE_SCOPE.team ? 'team' : 'mine'}`;

export const listMyTemplates = (signal?: AbortSignal): Promise<OutreachTemplate[]> =>
  getJson<OutreachTemplate[]>(`${BASE}/mine`, undefined, signal);

export const listTeamTemplates = (signal?: AbortSignal): Promise<TeamTemplates> =>
  getJson<TeamTemplates>(`${BASE}/team`, undefined, signal);

export const listPlaceholders = (signal?: AbortSignal): Promise<OutreachPlaceholder[]> =>
  getJson<OutreachPlaceholder[]>(`${BASE}/placeholders`, undefined, signal);

export const createTemplate = (scope: OutreachTemplateScope, input: OutreachTemplateInput): Promise<OutreachTemplate> =>
  sendJson<OutreachTemplate>('post', scopePath(scope), input);

export const updateTemplate = (template: OutreachTemplate, input: OutreachTemplateInput): Promise<OutreachTemplate> =>
  sendJson<OutreachTemplate>('patch', `${scopePath(template.scope)}/${template.id}`, {
    ...input,
    expectedVersion: template.version,
  });

export const removeTemplate = (template: OutreachTemplate): Promise<void> =>
  sendJson<void>('delete', `${scopePath(template.scope)}/${template.id}`);

export const copyTeamTemplate = (id: string): Promise<OutreachTemplate> =>
  sendJson<OutreachTemplate>('post', `${BASE}/team/${id}/copy`);

/** The rep's email signature from their Salesforce user; '' when none. */
export interface RepSignature {
  readonly signature: string;
  /** HTML (a logo, links) rather than plain lines. */
  readonly isHtml: boolean;
}

export const SIGNATURE_QUERY_KEY = ['outreach-templates', 'signature'] as const;

export const readSignature = (signal?: AbortSignal): Promise<RepSignature> =>
  getJson<RepSignature>(`${BASE}/signature`, undefined, signal);

/** How a placeholder is written in template text. */
export const placeholderToken = (key: string): string => `{${key}}`;

const PLACEHOLDER_PATTERN = /\{([a-z][a-z0-9_]*)\}/g;

/** A template's text with the samples filled in, and any placeholders nobody knows. */
export interface PreviewText {
  readonly text: string;
  readonly unknown: readonly string[];
}

/**
 * Fills a template with the placeholders' sample values for the editor's
 * preview, the way the API's `fillPlaceholders` fills a real client's: an
 * unknown placeholder stays as written, and is reported.
 *
 * @param template - Subject or body text.
 * @param placeholders - The known placeholders and their samples.
 * @returns The preview text and any unknown placeholders.
 */
export function previewText(template: string, placeholders: readonly OutreachPlaceholder[]): PreviewText {
  const samples = new Map(placeholders.map((placeholder) => [placeholder.key, placeholder.sample]));
  const unknown = new Set<string>();
  const text = template.replace(PLACEHOLDER_PATTERN, (match, key: string) => {
    const sample = samples.get(key);
    if (sample === undefined) {
      unknown.add(key);
      return match;
    }
    return sample;
  });
  return { text, unknown: [...unknown] };
}

/**
 * Inserts text into a field's value at the caret, replacing any selection.
 *
 * @param value - The field's current value.
 * @param insert - What to insert.
 * @param selection - The field's selection; the end of the value when unknown.
 * @returns The new value and where the caret goes after the insert.
 */
export function insertAt(
  value: string,
  insert: string,
  selection: { readonly start: number | null; readonly end: number | null },
): { readonly value: string; readonly caret: number } {
  const start = selection.start ?? value.length;
  const end = selection.end ?? start;
  return { value: value.slice(0, start) + insert + value.slice(end), caret: start + insert.length };
}
